import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import PageLayout, { PageSection } from "../../../components/navigation/PageLayout";
import Icon from "../../../components/AppIcon";
import { useTenantContext } from "../../../contexts/TenantContext";
import apiClient from "../../../services/api/client";

// ----- Colour options for departments ----------------------------------------
const COLOR_OPTIONS = [
  { color: "#3b82f6", name: "Blue" },
  { color: "#10b981", name: "Green" },
  { color: "#f59e0b", name: "Amber" },
  { color: "#ef4444", name: "Red" },
  { color: "#8b5cf6", name: "Purple" },
  { color: "#ec4899", name: "Pink" },
  { color: "#14b8a6", name: "Teal" },
  { color: "#6b7280", name: "Gray" },
];

const EMPTY_FORM = {
  name: "",
  code: "",
  description: "",
  colorCode: "#3b82f6",
  sortOrder: 0,
  isActive: true,
};

// ----- DepartmentModal -------------------------------------------------------
const DepartmentModal = ({ dept, onClose, onSave }) => {
  const isEdit = !!dept;
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dept) {
      setForm({
        name: dept.name || "",
        code: dept.code || "",
        description: dept.description || "",
        colorCode: dept.colorCode || "#3b82f6",
        sortOrder: dept.sortOrder ?? 0,
        isActive: dept.isActive ?? true,
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [dept]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Department name is required";
    if (form.sortOrder < 0) e.sortOrder = "Sort order must be 0 or greater";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field, val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({ ...form });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? "Edit Department" : "Create Department"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? "border-red-400" : "border-gray-300"
              }`}
              placeholder="e.g. Front of House"
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code
              <span className="ml-1 text-xs text-gray-400">(auto-generated if blank)</span>
            </label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => set("code", e.target.value.toUpperCase())}
              maxLength={20}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
              placeholder="e.g. FOH"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Optional description"
            />
          </div>

          {/* Colour + Sort order row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Colour
              </label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.color}
                    type="button"
                    title={c.name}
                    onClick={() => set("colorCode", c.color)}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${
                      form.colorCode === c.color
                        ? "border-gray-800 scale-110"
                        : "border-transparent"
                    }`}
                    style={{ backgroundColor: c.color }}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                min={0}
                value={form.sortOrder}
                onChange={(e) => set("sortOrder", parseInt(e.target.value, 10) || 0)}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.sortOrder ? "border-red-400" : "border-gray-300"
                }`}
              />
              {errors.sortOrder && (
                <p className="mt-1 text-xs text-red-600">{errors.sortOrder}</p>
              )}
            </div>
          </div>

          {/* Active toggle (edit only) */}
          {isEdit && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set("isActive", !form.isActive)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.isActive ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.isActive ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700">
                {form.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="dept-form"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Department"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ----- Main Page -------------------------------------------------------------
const DepartmentsPage = () => {
  const { selectedTenant, isGlobalAdmin: isGlobalAdminContext } = useTenantContext();

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [notification, setNotification] = useState(null);

  const [selectedDept, setSelectedDept] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const shouldFetchData = !isGlobalAdminContext || selectedTenant;

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  };

  // ---- Fetch ----------------------------------------------------------------
  const fetchDepartments = async () => {
    if (!shouldFetchData) return;
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (!showInactive) params.activeOnly = true;
      if (isGlobalAdminContext && selectedTenant?.id) params.tenantId = selectedTenant.id;

      const data = await apiClient.get("/departments", params);
      if (data.success) {
        setDepartments(data.data || []);
      } else {
        throw new Error(data.message || "Failed to fetch departments");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch departments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldFetchData) fetchDepartments();
  }, [showInactive, selectedTenant?.id, shouldFetchData]);

  // ---- Filter ---------------------------------------------------------------
  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return departments.filter(
      (d) =>
        !term ||
        d.name.toLowerCase().includes(term) ||
        (d.code && d.code.toLowerCase().includes(term)) ||
        (d.description && d.description.toLowerCase().includes(term)),
    );
  }, [departments, searchTerm]);

  // ---- CRUD handlers --------------------------------------------------------
  const handleSave = async (formData) => {
    try {
      const params = {};
      if (isGlobalAdminContext && selectedTenant?.id) params.tenantId = selectedTenant.id;

      let result;
      if (selectedDept) {
        result = await apiClient.put(`/departments/${selectedDept.id}`, formData);
      } else {
        result = await apiClient.post("/departments", formData, params);
      }

      if (!result.success) throw new Error(result.message || "Save failed");

      showNotification(
        selectedDept ? "Department updated successfully." : "Department created successfully.",
      );
      setShowModal(false);
      setSelectedDept(null);
      await fetchDepartments();
    } catch (err) {
      showNotification(err.message || "Failed to save department.", "error");
    }
  };

  const handleDeleteConfirm = async (id) => {
    try {
      const result = await apiClient.delete(`/departments/${id}`);
      if (!result.success) throw new Error(result.message || "Delete failed");
      showNotification("Department deleted successfully.");
      await fetchDepartments();
    } catch (err) {
      showNotification(err.message || "Failed to delete department.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  // ---- Render ---------------------------------------------------------------
  const breadcrumbs = [
    { label: "Organisation Settings", path: "/system/job-roles" },
    { label: "Departments", path: "/system/departments" },
  ];

  return (
    <PageLayout>
      <Helmet>
        <title>Departments · Rocket TRONC</title>
      </Helmet>

      {/* Notification toast */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${
            notification.type === "error" ? "bg-red-600" : "bg-green-600"
          }`}
        >
          <Icon name={notification.type === "error" ? "AlertCircle" : "CheckCircle"} size={16} />
          {notification.message}
        </div>
      )}

      <PageSection breadcrumbs={breadcrumbs}>
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Departments</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage departments that group job roles for distribution rules.
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedDept(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            <Icon name="Plus" size={16} />
            New Department
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Icon
              name="Search"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search departments…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded"
            />
            Show inactive
          </label>
        </div>

        {/* Content */}
        {!shouldFetchData ? (
          <div className="text-center py-12 text-gray-500">
            <Icon name="Building2" size={40} className="mx-auto mb-3 text-gray-300" />
            <p>Select a tenant to manage departments.</p>
          </div>
        ) : loading ? (
          <div className="text-center py-12 text-gray-500">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />
            <p>Loading departments…</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            <Icon name="AlertCircle" size={16} className="inline mr-2" />
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Icon name="Building2" size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium">
              {searchTerm ? "No departments match your search" : "No departments yet"}
            </p>
            {!searchTerm && (
              <p className="text-sm mt-1">
                Create your first department to start linking job roles.
              </p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Summary bar */}
            <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 text-sm text-gray-500">
              {filtered.length} department{filtered.length !== 1 ? "s" : ""}
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-6 py-3 text-left font-medium">Department</th>
                  <th className="px-6 py-3 text-left font-medium">Code</th>
                  <th className="px-6 py-3 text-left font-medium hidden md:table-cell">
                    Description
                  </th>
                  <th className="px-6 py-3 text-center font-medium">Job Roles</th>
                  <th className="px-6 py-3 text-center font-medium">Status</th>
                  <th className="px-6 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((dept) => (
                  <tr key={dept.id} className="hover:bg-gray-50 transition-colors">
                    {/* Name + colour */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: dept.colorCode || "#6b7280" }}
                        />
                        <span className="font-medium text-gray-900">{dept.name}</span>
                      </div>
                    </td>

                    {/* Code */}
                    <td className="px-6 py-4">
                      {dept.code ? (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono">
                          {dept.code}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* Description */}
                    <td className="px-6 py-4 hidden md:table-cell text-gray-500 max-w-xs truncate">
                      {dept.description || "—"}
                    </td>

                    {/* Job role count */}
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 text-gray-700">
                        <Icon name="Briefcase" size={13} className="text-gray-400" />
                        {dept.jobRoleCount ?? 0}
                      </span>
                    </td>

                    {/* Status badge */}
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          dept.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {dept.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setSelectedDept(dept);
                            setShowModal(true);
                          }}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Icon name="Pencil" size={15} />
                        </button>
                        <button
                          onClick={() => setDeletingId(dept.id)}
                          disabled={dept.jobRoleCount > 0}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={
                            dept.jobRoleCount > 0
                              ? "Cannot delete — department has linked job roles"
                              : "Delete"
                          }
                        >
                          <Icon name="Trash2" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageSection>

      {/* Create / Edit modal */}
      {showModal && (
        <DepartmentModal
          dept={selectedDept}
          onClose={() => {
            setShowModal(false);
            setSelectedDept(null);
          }}
          onSave={handleSave}
        />
      )}

      {/* Delete confirmation */}
      {deletingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Icon name="Trash2" size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Department</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-6">
              Are you sure you want to delete this department?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteConfirm(deletingId)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default DepartmentsPage;
